/**
 * Referral agreement enums for B2B relationships
 * @see Issue #238 - Partner Companies B2B Model
 */

/**
 * Type of referral agreement between businesses
 */
export enum ReferralAgreementType {
  REFERRAL = 'referral', // Simple referral fee
  CORRESPONDENT = 'correspondent', // Originate, other funds
  WHOLESALE = 'wholesale', // Wholesale pricing
  WHITE_LABEL = 'white_label', // Branded for referrer
}

/**
 * Status of a referral agreement.
 *
 * Must match the CRM service's storage values (see
 * `34-crm-service/prisma/schema.prisma` Referral Agreement `status` column:
 * `draft, pending_approval, pending_signature, active, suspended, terminated, expired`).
 *
 * `PENDING_SIGNATURE` (Issue #1531 Phase 6) — the e-sign envelope was sent
 * to recipients (B1's lender_signer + B2's partner_signer) and we're
 * awaiting all signatures. Webhook handler flips this to ACTIVE on
 * completion. Inserted between PENDING_APPROVAL and ACTIVE because the
 * partnership-e-sign flow runs *after* both businesses approve and *before*
 * the agreement becomes active.
 */
export enum ReferralAgreementStatus {
  DRAFT = 'draft',
  PENDING_APPROVAL = 'pending_approval',
  PENDING_SIGNATURE = 'pending_signature',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  TERMINATED = 'terminated',
  EXPIRED = 'expired',
}

/**
 * When commission is triggered/earned
 */
export enum CommissionTrigger {
  FUNDING = 'funding', // When loan funds
  CLOSING = 'closing', // When loan closes
  FIRST_PAYMENT = 'first_payment', // After first payment received
  APPLICATION = 'application', // On application submission (rare)
}

/**
 * Payment terms for commission
 */
export enum PaymentTerms {
  NET_15 = 'net_15',
  NET_30 = 'net_30',
  NET_45 = 'net_45',
  NET_60 = 'net_60',
  IMMEDIATE = 'immediate',
}

/**
 * Scope of exclusivity in agreement
 */
export enum ExclusivityScope {
  TERRITORY = 'territory',
  PRODUCT = 'product',
  BOTH = 'both',
  NONE = 'none',
}

/**
 * Agreement type display names
 */
export const REFERRAL_AGREEMENT_TYPE_NAMES: Record<ReferralAgreementType, string> = {
  [ReferralAgreementType.REFERRAL]: 'Referral',
  [ReferralAgreementType.CORRESPONDENT]: 'Correspondent',
  [ReferralAgreementType.WHOLESALE]: 'Wholesale',
  [ReferralAgreementType.WHITE_LABEL]: 'White Label',
};

/**
 * Agreement status display names
 */
export const REFERRAL_AGREEMENT_STATUS_NAMES: Record<ReferralAgreementStatus, string> = {
  [ReferralAgreementStatus.DRAFT]: 'Draft',
  [ReferralAgreementStatus.PENDING_APPROVAL]: 'Pending Approval',
  [ReferralAgreementStatus.PENDING_SIGNATURE]: 'Pending Signature',
  [ReferralAgreementStatus.ACTIVE]: 'Active',
  [ReferralAgreementStatus.SUSPENDED]: 'Suspended',
  [ReferralAgreementStatus.TERMINATED]: 'Terminated',
  [ReferralAgreementStatus.EXPIRED]: 'Expired',
};

/**
 * Commission trigger display names
 */
export const COMMISSION_TRIGGER_NAMES: Record<CommissionTrigger, string> = {
  [CommissionTrigger.FUNDING]: 'On Funding',
  [CommissionTrigger.CLOSING]: 'On Closing',
  [CommissionTrigger.FIRST_PAYMENT]: 'After First Payment',
  [CommissionTrigger.APPLICATION]: 'On Application',
};

/**
 * Payment terms display names
 */
export const PAYMENT_TERMS_NAMES: Record<PaymentTerms, string> = {
  [PaymentTerms.NET_15]: 'Net 15',
  [PaymentTerms.NET_30]: 'Net 30',
  [PaymentTerms.NET_45]: 'Net 45',
  [PaymentTerms.NET_60]: 'Net 60',
  [PaymentTerms.IMMEDIATE]: 'Immediate',
};
